/**
 * CommunityGalleryPage — public list of photo albums for a given community.
 * Renders each album as a polaroid-style card with cover, title, photo count
 * or iCloud badge, and links to the album detail (or opens iCloud in a tab).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COPY = {
  ca: { back: '← Tornar', title: 'Galeria', subtitle: 'Records i moments compartits.', empty: 'Encara no hi ha àlbums.', photos: 'fotos', openIcloud: 'Obrir a iCloud ↗' },
  es: { back: '← Volver', title: 'Galería', subtitle: 'Recuerdos y momentos compartidos.', empty: 'Aún no hay álbumes.', photos: 'fotos', openIcloud: 'Abrir en iCloud ↗' },
  en: { back: '← Back', title: 'Gallery', subtitle: 'Shared memories and moments.', empty: 'No albums yet.', photos: 'photos', openIcloud: 'Open in iCloud ↗' },
  fr: { back: '← Retour', title: 'Galerie', subtitle: 'Souvenirs et moments partagés.', empty: 'Pas encore d\'albums.', photos: 'photos', openIcloud: 'Ouvrir sur iCloud ↗' },
};
const ROTATIONS = ['-2deg', '1.5deg', '-1.2deg', '2.1deg', '-1.8deg', '1.6deg'];

export const CommunityGalleryPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { community } = useParams();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const copy = COPY[(i18n.language || 'ca').split('-')[0]] || COPY.ca;

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/${community}/gallery/albums`)
      .then((r) => setAlbums(r.data || []))
      .catch(() => setAlbums([]))
      .finally(() => setLoading(false));
  }, [community]);

  const openAlbum = (album) => {
    if (album.kind === 'icloud' && album.icloud_url) {
      window.open(album.icloud_url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(`/${community}/galeria/${album.id}`);
    }
  };

  const titleFor = (album) => album.title?.[i18n.language.split('-')[0]] || album.title?.ca || album.title?.es || 'Àlbum';

  return (
    <div className="min-h-screen bg-[#0E4D2A] text-[#F2E8D5] pb-24" data-testid="community-gallery-page">
      <div className="max-w-6xl mx-auto px-5 md:px-10 pt-8 md:pt-12">
        <button
          type="button"
          onClick={() => navigate(`/${community}`)}
          className="text-[#A8B4A6] hover:text-[#D4AF37] text-sm tracking-[0.2em] font-sans transition-colors"
          data-testid="gallery-back-btn"
        >
          {copy.back}
        </button>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center mt-6 mb-12"
        >
          <div className="font-sans text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]">📷 {copy.title}</div>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">{copy.title}</h1>
          <p className="mt-3 text-[#A8B4A6] italic text-sm md:text-base">{copy.subtitle}</p>
        </motion.div>

        {loading ? (
          <div className="text-center text-[#A8B4A6] py-20">…</div>
        ) : albums.length === 0 ? (
          <div className="text-center text-[#A8B4A6]/70 italic py-16" data-testid="gallery-empty">{copy.empty}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {albums.map((a, i) => (
              <motion.button
                key={a.id}
                type="button"
                onClick={() => openAlbum(a)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                whileHover={{ y: -6, rotate: 0, scale: 1.02 }}
                style={{ transform: `rotate(${ROTATIONS[i % ROTATIONS.length]})`, transformOrigin: 'center top' }}
                className="group relative bg-[#FBF7EE] text-[#1a1a1a] rounded-md p-3 pb-6 shadow-[0_10px_30px_rgba(0,0,0,0.35)] hover:shadow-[0_18px_40px_rgba(0,0,0,0.45)] transition-shadow duration-500"
                data-testid={`gallery-album-${a.id}`}
              >
                <div className="aspect-square w-full bg-gradient-to-br from-[#083B1F] to-[#0E4D2A] rounded-sm overflow-hidden relative">
                  {a.cover_image ? (
                    <img
                      src={a.cover_image.startsWith('/api/') ? `${API_URL}${a.cover_image}` : a.cover_image}
                      alt={titleFor(a)}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.opacity = 0.2; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-6xl select-none">📷</div>
                  )}
                  {a.kind === 'icloud' && (
                    <span className="absolute top-2 right-2 bg-white/90 text-[#0E4D2A] text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full font-sans font-bold">
                      ☁️ iCloud
                    </span>
                  )}
                </div>
                <div className="mt-3 px-1 text-center">
                  <div style={{ fontFamily: "'Caveat', cursive", fontSize: '1.4rem', fontWeight: 600 }} className="leading-tight">
                    {titleFor(a)}
                  </div>
                  <div className="text-xs text-[#666] mt-1 font-sans">
                    {a.kind === 'icloud'
                      ? copy.openIcloud
                      : `${a.photo_count || 0} ${copy.photos}`}
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityGalleryPage;
